import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

const DEFAULT_FORM = {
  versionId: "",
  deviceId: "",
  result: "passed",
  executedAt: "",
  source: "manual",
  externalRunId: "",
  durationMs: "",
  note: "",
  log: ""
};

const resultOptions = [
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "blocked", label: "Blocked" },
  { value: "not_run", label: "Not run" },
  { value: "inconclusive", label: "Inconclusive" }
];

function toDateTimeLocal(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";

  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

export default function TestCaseRunFormModal({ show, onClose, onSaved, testCase, initialValues = {} }) {
  const [versions, setVersions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const sortedVersions = useMemo(() => {
    return [...versions].sort((leftVersion, rightVersion) => {
      const activeCompare = Number(Boolean(rightVersion.isActive)) - Number(Boolean(leftVersion.isActive));
      if (activeCompare !== 0) return activeCompare;

      const closedCompare = Number(Boolean(leftVersion.closed)) - Number(Boolean(rightVersion.closed));
      if (closedCompare !== 0) return closedCompare;

      return String(leftVersion.name ?? "").localeCompare(
        String(rightVersion.name ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    });
  }, [versions]);

  const sortedDevices = useMemo(() => {
    return [...devices].sort((leftDevice, rightDevice) => (
      String(leftDevice.name ?? "").localeCompare(
        String(rightDevice.name ?? ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      )
    ));
  }, [devices]);

  useEffect(() => {
    if (!show) {
      setVersions([]);
      setDevices([]);
      setForm(DEFAULT_FORM);
      setSaving(false);
      setError("");
      return;
    }

    let cancelled = false;

    const loadLookups = async () => {
      try {
        const [versionsRes, devicesRes] = await Promise.all([
          fetchWithAuth("/api/versions"),
          fetchWithAuth("/api/devices")
        ]);

        if (!versionsRes.ok || !devicesRes.ok) {
          throw new Error("Failed to load versions or devices");
        }

        const versionsData = await versionsRes.json();
        const devicesData = await devicesRes.json();

        if (!cancelled) {
          setVersions(versionsData);
          setDevices(devicesData);
        }
      } catch (err) {
        if (!cancelled) {
          setVersions([]);
          setDevices([]);
          setError(err.message);
        }
      }
    };

    setForm({
      ...DEFAULT_FORM,
      ...initialValues,
      executedAt: toDateTimeLocal()
    });
    setError("");
    setSaving(false);
    loadLookups();

    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;

    setForm((current) => ({
      ...current,
      versionId: current.versionId || initialValues.versionId || sortedVersions.find((version) => version.isActive)?.id || sortedVersions[0]?.id || "",
      deviceId: current.deviceId || initialValues.deviceId || sortedDevices[0]?.id || ""
    }));
  }, [show, sortedVersions, sortedDevices, initialValues.versionId, initialValues.deviceId]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const saveRun = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      versionId: form.versionId ? Number(form.versionId) : null,
      deviceId: form.deviceId ? Number(form.deviceId) : null,
      result: form.result,
      executedAt: form.executedAt || null,
      source: form.source,
      externalRunId: form.externalRunId.trim() || null,
      durationMs: form.durationMs === "" ? null : Number(form.durationMs),
      note: form.note.trim() || null,
      log: form.log.trim() || null
    };

    try {
      const res = await fetchWithAuth(`/api/test-cases/${testCase.id}/runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Save failed");
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Form onSubmit={saveRun}>
        <Modal.Header closeButton>
          <Modal.Title>Add run - {testCase?.number}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <div className="alert alert-danger py-2">{error}</div>
          )}

          <div className="text-muted mb-3">{testCase?.name}</div>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Version</Form.Label>
                <Form.Select
                  value={form.versionId}
                  onChange={(event) => updateField("versionId", event.target.value)}
                  required
                >
                  <option value="">Select version</option>
                  {sortedVersions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Device</Form.Label>
                <Form.Select
                  value={form.deviceId}
                  onChange={(event) => updateField("deviceId", event.target.value)}
                  required
                >
                  <option value="">Select device</option>
                  {sortedDevices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Result</Form.Label>
                <Form.Select
                  value={form.result}
                  onChange={(event) => updateField("result", event.target.value)}
                  required
                >
                  {resultOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Executed at</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.executedAt}
                  onChange={(event) => updateField("executedAt", event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Duration ms</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={form.durationMs}
                  onChange={(event) => updateField("durationMs", event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Source</Form.Label>
                <Form.Select
                  value={form.source}
                  onChange={(event) => updateField("source", event.target.value)}
                >
                  <option value="manual">Manual</option>
                  <option value="automated">Automated</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>External run ID</Form.Label>
                <Form.Control
                  value={form.externalRunId}
                  onChange={(event) => updateField("externalRunId", event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label>Note</Form.Label>
                <Form.Control
                  value={form.note}
                  onChange={(event) => updateField("note", event.target.value)}
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label>Log</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={form.log}
                  onChange={(event) => updateField("log", event.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={saving || !sortedVersions.length || !sortedDevices.length}
          >
            {saving ? "Saving..." : "Save run"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
